import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../context/StateContext";
import { Archive } from "../../../../../models/archive";
import { Error } from "../../../../../models/error";
import { InvoicesShipment } from "../../../../../models/invoices.shipment";
import { Filters, Searches } from "../../../../../models/shipeezi.table";
import { ShipmentInvoiceService } from "../../../../../services/shipment.invoice.service";
import { checkError } from "../../../../../util/checkError";
import { CodeError } from "../../../../../util/enums";
import { ShipmentInvoicesKeys } from "../../../../../util/queries";
import { getRelatedFilters } from "../../../Shared/Report/Maintenance/SupplyChain/utils";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour

interface SaveInvoiceProps {
  invoices: InvoicesShipment[];
  archive: Archive;
  uuid: string;
}

const useAction = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const getInvoiceView = (invoiceId: number) => {
    return useQuery({
      queryKey: ShipmentInvoicesKeys.getById(invoiceId),
      queryFn: async () => {
        const response = await ShipmentInvoiceService.getById(invoiceId, {
          include: JSON.stringify([
            {
              association: "invoiceShipment",
            },
          ]),
        });

        return response.data;
      },
      enabled: !!currentUser?.id && !!invoiceId,
      staleTime: 3 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getAllInvoices = (
    page: number = 1,
    pageSize: number = 20,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      // TODO: remove initialData when publishing
      // mocks are just for testing purposes
      // initialData: invoices,
      queryKey: ShipmentInvoicesKeys.getAndCountAll([page, pageSize, searches, filters]),
      queryFn: async () => {
        const response = await ShipmentInvoiceService.getAndCountAll({
          page,
          pageSize,
          groupBy: JSON.stringify(["invoices.id"]),
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          ...getRelatedFilters(filters),
          attributes: [
            "id",
            "invoiceNumber",
            "description",
            "invoiceDate",
            "invoiceDueDate",
            "autoRate",
            "gstAmount",
            "invoicedLocalAmount",
            "exchangeRate",
            "exchangeRateCurrency",
            "chargeCode",
            "overseasAmount",
          ],
          include: JSON.stringify([
            {
              association: "invoiceShipment",
              where: getRelatedFilters(filters, "invoiceShipment"),
            },
          ]),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.id,
      staleTime: 3 * 60 * 1000, // 3 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const useSaveInvoices = (callback?: () => void) => {
    return useMutation({
      mutationKey: ShipmentInvoicesKeys.uploadInvoices(),
      mutationFn: ({ invoices, archive, uuid }: SaveInvoiceProps) => {
        return ShipmentInvoiceService.uploadInvoices({
          invoices,
          archive,
          uuid,
        });
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (
          errorAxios?.response?.data &&
          errorAxios.response.data.error === CodeError.INVALID_CSV
        ) {
          return;
        }

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
      },
    });
  };

  const useDeleteInvoices = (id: number, callback?: () => void) => {
    return useMutation({
      mutationKey: ShipmentInvoicesKeys.delete(id),
      mutationFn: (id: number) => {
        return ShipmentInvoiceService.delete(id);
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
      },
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callback) {
          callback();
        }
      },
    });
  };

  return {
    getAllInvoices,
    getInvoiceView,
    useSaveInvoices,
    useDeleteInvoices,
  };
};

export default useAction;
